import type { Paragraph } from '@/api/types/stowed-element'
import type { DropCapSelectionSummary } from '@/lib/text/types/drop-cap-selection-summary'
import type { DropCapStyle } from '@/lib/text/types/drop-cap-style'
import type { DropCapWireValue } from './types/drop-cap-wire-value'
import type { DropCapFieldValidation } from './types/drop-cap-field-validation'
import type { DropCapOffsetPresetName } from './types/drop-cap-offset-preset-name'

export const DROP_CAP_LINES_MIN = 1
export const DROP_CAP_LINES_MAX = 10
export const DROP_CAP_OFFSET_MIN = 1
export const DROP_CAP_OFFSET_MAX = 24
export const DROP_CAP_DEFAULT_LINES = 1
export const DROP_CAP_DEFAULT_OFFSET = 1

export const DROP_CAP_LINES_RANGE_MESSAGE = `Value: ${DROP_CAP_LINES_MIN}-${DROP_CAP_LINES_MAX}`
export const DROP_CAP_OFFSET_RANGE_MESSAGE = `Value: ${DROP_CAP_OFFSET_MIN}-${DROP_CAP_OFFSET_MAX}`
export const DROP_CAP_INVALID_VALUE_MESSAGE = 'Invalid value'

/** Normalizes a stored/incoming drop-cap value to the wire's own vocabulary. Also accepts the
 *  long-form 'INSIDE'/'OUTSIDE' some design docs and fixtures use, since they name the same state
 *  under a different spelling — anything else (including missing/undefined) normalizes to 'NONE'. */
export function normalizeDropCapValue(value: unknown): DropCapWireValue {
  if (value === 'INS' || value === 'INSIDE') return 'INS'
  if (value === 'OUT' || value === 'OUTSIDE') return 'OUT'
  return 'NONE'
}

/**
 * Clamp Lines to the legal 1-10 integer range, rounding a fractional input. Falls back to the
 * default for a non-finite input, and also for anything below the legal minimum (including `0`) —
 * used for sanitizing stored/legacy data, not for validating live user input (see
 * `validateDropCapLinesInput` for that).
 *
 * Below-minimum input is treated as "unset", not clamped up to the floor: protobuf has no scalar
 * "unset" distinct from the zero value (`uint32 drop_cap_lines`), so a paragraph that never had an
 * explicit Lines value decodes it as a real `0` — clamping that up to `1` would show the wrong
 * default the moment the paragraph's drop cap becomes active, since `1` is a value the user could
 * have legitimately chosen and `0` never is (the legal range starts at 1).
 */
export function clampDropCapLines(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)

  if (!Number.isFinite(n) || n < DROP_CAP_LINES_MIN) return DROP_CAP_DEFAULT_LINES

  return Math.min(DROP_CAP_LINES_MAX, Math.round(n))
}

/** Clamp Offset to the legal 1-24 integer range — continuous, every integer in range is valid,
 *  rounding a fractional input. Falls back to the default for a non-finite input and for anything
 *  below the legal minimum (including `0`) — see `clampDropCapLines` for why below-minimum input
 *  is treated as unset rather than clamped up to the floor. */
export function clampDropCapOffset(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)

  if (!Number.isFinite(n) || n < DROP_CAP_OFFSET_MIN) return DROP_CAP_DEFAULT_OFFSET

  return Math.min(DROP_CAP_OFFSET_MAX, Math.round(n))
}

function validateIntegerInRange(raw: string, min: number, max: number): DropCapFieldValidation {
  if (raw.trim() === '') return { kind: 'invalid' }

  const n = Number(raw)

  if (!Number.isFinite(n)) return { kind: 'invalid' }

  const rounded = Math.round(n)

  if (rounded < min || rounded > max) {
    return { kind: 'range', clampedValue: Math.min(max, Math.max(min, rounded)) }
  }

  return { kind: 'valid', value: rounded }
}

/** Validates a Lines numeric-input string, distinguishing non-numeric input from an out-of-range
 *  number — the panel shows a different message for each (`DROP_CAP_INVALID_VALUE_MESSAGE` vs
 *  `DROP_CAP_LINES_RANGE_MESSAGE`). An out-of-range-but-parseable value (`'range'`) carries
 *  `clampedValue` so the panel can clamp-and-apply it rather than leaving it unapplied — an
 *  unparseable one (`'invalid'`) has no number to clamp to, so it's left for the user to fix. */
export function validateDropCapLinesInput(raw: string): DropCapFieldValidation {
  return validateIntegerInRange(raw, DROP_CAP_LINES_MIN, DROP_CAP_LINES_MAX)
}

/** Validates an Offset numeric-input string. See `validateDropCapLinesInput`. */
export function validateDropCapOffsetInput(raw: string): DropCapFieldValidation {
  return validateIntegerInRange(raw, DROP_CAP_OFFSET_MIN, DROP_CAP_OFFSET_MAX)
}

export const DROP_CAP_OFFSET_PRESET_NAMES: readonly DropCapOffsetPresetName[] = [
  'Narrow',
  'Natural',
  'Wide',
]

/** Offset-preset multipliers against the paragraph's own font size. */
const DROP_CAP_OFFSET_PRESET_FACTORS: Record<DropCapOffsetPresetName, number> = {
  Narrow: 0.25,
  Natural: 0.75,
  Wide: 1.5,
}

/** Offset value a preset chip computes for a given font size — rounded and clamped to the legal
 *  1-24 range, same as any other Offset edit. There is no font-size-derived ceiling above 24: a
 *  large enough font size (e.g. a 32pt run's "Wide" chip, 32 * 1.5 = 48) clamps down to the flat
 *  max like any other out-of-range value, the same as a typed 48 would. The chips are a shortcut
 *  for setting Offset, not a separate stored property (there is no fourth drop-cap field on the
 *  wire). */
export function computeDropCapPresetOffset(
  preset: DropCapOffsetPresetName,
  fontSizePt: number
): number {
  return clampDropCapOffset(fontSizePt * DROP_CAP_OFFSET_PRESET_FACTORS[preset])
}

/** Which preset chip (if any) the given Offset currently matches, for highlighting. Presets are
 *  computed from font size rather than stored, so the active chip is derived by re-computing each
 *  one and comparing — not read back from any persisted "which preset was clicked" state. */
export function matchDropCapOffsetPreset(
  offset: number,
  fontSizePt: number
): DropCapOffsetPresetName | null {
  for (const name of DROP_CAP_OFFSET_PRESET_NAMES) {
    if (computeDropCapPresetOffset(name, fontSizePt) === offset) return name
  }

  return null
}

const EMPTY_DROP_CAP_SUMMARY: DropCapSelectionSummary = {
  hasDropCap: null,
  style: null,
  lines: null,
  offset: null,
}

/**
 * Summarizes drop-cap state across a whole-box (Select-Tool, no caret) paragraph selection — the
 * basis for the panel's Mixed display.
 *
 * `style`/`lines`/`offset` are computed over the drop-cap-bearing paragraphs alone: a paragraph with
 * no active drop cap only affects `hasDropCap` — it has no style, Lines, or Offset of its own to
 * disagree with, even if it happens to still carry a leftover stored Lines/Offset value from before
 * it was last turned off (Lines/Offset are "meaningful only when dropCap !== 'NONE'", see
 * `stowed-element.ts`). This means a selection with exactly one drop-cap paragraph is never "mixed"
 * on style/lines/offset by this function's own accounting — there's nothing to disagree with.
 *
 * The panel itself layers a further, UI-level policy on top of `hasDropCap === null` (some
 * paragraphs have a drop cap, some don't) that this function does NOT apply: it always shows
 * Lines/Offset as Mixed in that case, regardless of what this function computed for `lines`/
 * `offset` — a paragraph with no active drop cap reads to the user as having no Lines/Offset
 * setting at all, not as silently agreeing via a value it never meaningfully had. See
 * `applyDropCapSummary` in `type-panel/hooks/use-drop-cap.ts`.
 */
export function summarizeDropCapSelection(paragraphs: Paragraph[]): DropCapSelectionSummary {
  if (paragraphs.length === 0) return EMPTY_DROP_CAP_SUMMARY

  let sawOn = false
  let sawOff = false
  let style: DropCapStyle | null = null
  let styleMixed = false
  let lines: number | null = null
  let linesMixed = false
  let offset: number | null = null
  let offsetMixed = false

  for (const paragraph of paragraphs) {
    const value = normalizeDropCapValue(paragraph.dropCap)

    if (value === 'NONE') {
      sawOff = true
      continue
    }

    sawOn = true

    if (style === null) style = value
    else if (style !== value) styleMixed = true

    const paraLines = clampDropCapLines(paragraph.dropCapLines)

    if (lines === null) lines = paraLines
    else if (lines !== paraLines) linesMixed = true

    const paraOffset = clampDropCapOffset(paragraph.dropCapOffset)

    if (offset === null) offset = paraOffset
    else if (offset !== paraOffset) offsetMixed = true
  }

  return {
    hasDropCap: sawOn && sawOff ? null : sawOn,
    style: styleMixed ? null : style,
    lines: linesMixed ? null : lines,
    offset: offsetMixed ? null : offset,
  }
}
